import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  ArrowDownToLine,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clipboard,
  ClipboardPaste,
  CircleDot,
  Loader2,
  MoreHorizontal,
  Pause,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import type { BespokeTemplateResponse } from '../../services/api/types.gen';
import type {
  FormTreeQuestion,
  SectionGenerationStatus,
  SectionLayoutState,
} from './formSchemaTypes';
import {
  formatRemotePushTimeLabel,
  getSectionLayoutState,
  resolveBespokePrimaryAndStandby,
  type RemotePushInfo,
} from './formSchemaUtils';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { SectionTemplateDetailsEdit } from './SectionTemplateDetailsEdit';
import { useMemo, useState } from 'react';

type BespokeStatus = BespokeTemplateResponse['status'];

function SectionLayoutValidationLabel({
  state,
}: {
  state: SectionLayoutState;
}) {
  if (state.kind === 'valid') {
    if (state.template.status === 'paused') {
      return (
        <span
          className='inline-flex items-center gap-1 text-xs font-medium text-amber-700'
          title='Paused custom layout'
        >
          <Pause className='h-3.5 w-3.5 shrink-0' aria-hidden />
          Valid
        </span>
      );
    }
    return (
      <span
        className='inline-flex items-center gap-1 text-xs font-medium text-green-700'
        title='Layout passes section validation'
      >
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0' aria-hidden />
        Valid
      </span>
    );
  }
  if (state.kind === 'invalid') {
    return (
      <span
        className='inline-flex items-center gap-1 text-xs font-medium text-destructive'
        title={state.reason}
      >
        <AlertTriangle className='h-3.5 w-3.5 shrink-0' aria-hidden />
        Invalid
      </span>
    );
  }
  return (
    <span
      className='inline-flex items-center gap-1 text-xs font-medium text-muted-foreground'
      title='No custom layout'
    >
      <CircleDashed className='h-3.5 w-3.5 shrink-0' aria-hidden />
      Missing
    </span>
  );
}

/** Small dot indicating an MCP-pushed change not yet opened in the editor (EMR-277). */
function RemotePushDot({ info }: { info: RemotePushInfo }) {
  return (
    <span
      className='inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500'
      title={`Pushed remotely · ${formatRemotePushTimeLabel(info.updatedAt)}`}
    />
  );
}

function StatusBadge({ status }: { status: BespokeStatus }) {
  if (status === 'live') {
    return (
      <Badge
        className='border-transparent bg-emerald-600 text-white hover:bg-emerald-600'
        variant='default'
      >
        Live
      </Badge>
    );
  }
  if (status === 'paused') {
    return (
      <Badge
        className='border-transparent bg-amber-600 text-white hover:bg-amber-600'
        variant='default'
      >
        Paused
      </Badge>
    );
  }
  return (
    <Badge variant='outline' className='font-normal text-muted-foreground'>
      Standby
    </Badge>
  );
}

/** Menus portal into the dialog panel when provided so they work inside modal dialogs. */
const DROPDOWN_MENU_CONTENT_CLASS = 'z-[100]';

/** Actions for the active-template card or a standby row (variant controls menu items). */
function TemplateActionsMenu({
  template,
  variant,
  statusControlsDisabled,
  menuPortalContainer,
  onEditTemplate,
  onStartEditDetails,
  onCopyTemplate,
  onPasteTemplate,
  onDeleteTemplate,
  onSetTemplateStatus,
}: {
  template: BespokeTemplateResponse;
  variant: 'active' | 'standby';
  statusControlsDisabled: boolean;
  menuPortalContainer?: HTMLElement | null;
  onEditTemplate: (t: BespokeTemplateResponse) => void;
  onStartEditDetails: (t: BespokeTemplateResponse) => void;
  onCopyTemplate: (t: BespokeTemplateResponse) => void;
  onPasteTemplate: (t: BespokeTemplateResponse) => void;
  onDeleteTemplate: (t: BespokeTemplateResponse) => void;
  onSetTemplateStatus: (
    t: BespokeTemplateResponse,
    status: BespokeStatus
  ) => void;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-8 w-8 shrink-0 p-0'
          aria-label='Template actions'
        >
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        container={menuPortalContainer ?? undefined}
        className={DROPDOWN_MENU_CONTENT_CLASS}
      >
        {variant === 'active' ? (
          <>
            <DropdownMenuItem
              disabled={statusControlsDisabled}
              onClick={() => onSetTemplateStatus(template, 'standby')}
            >
              <ArrowDownToLine className='h-3.5 w-3.5 mr-2' />
              Move to standby
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {variant === 'standby' ? (
          <>
            <DropdownMenuItem
              disabled={statusControlsDisabled}
              onClick={() => onSetTemplateStatus(template, 'live')}
            >
              <CircleDot className='h-3.5 w-3.5 mr-2' />
              Set active
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem onClick={() => onEditTemplate(template)}>
          <Pencil className='h-3.5 w-3.5 mr-2' />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onStartEditDetails(template)}>
          <Pencil className='h-3.5 w-3.5 mr-2' />
          Edit name &amp; description…
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onCopyTemplate(template)}
          disabled={!template.layout_json}
        >
          <Clipboard className='h-3.5 w-3.5 mr-2' />
          Copy layout
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onPasteTemplate(template)}>
          <ClipboardPaste className='h-3.5 w-3.5 mr-2' />
          Paste layout
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onDeleteTemplate(template)}
          className='text-destructive'
        >
          <Trash2 className='h-3.5 w-3.5 mr-2' />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SectionTemplatesTable({
  sectionDisplayName,
  templates,
  questions,
  generationStatus,
  busyTemplateId,
  getRemotePush,
  menuPortalContainer,
  onCreateLayout,
  onGenerateLayout,
  onGenerateLayoutWithCustomPrompt,
  onAddLayoutFromClipboard,
  onEditTemplate,
  onEditTemplateDetails,
  onCopyTemplate,
  onPasteTemplate,
  onSetTemplateStatus,
  onDeleteTemplate,
}: {
  sectionDisplayName: string;
  templates: BespokeTemplateResponse[];
  /** Section schema questions — when provided, each row shows layout validation. */
  questions?: FormTreeQuestion[];
  generationStatus?: SectionGenerationStatus;
  /** Disables status / promote controls while a PATCH is in flight */
  busyTemplateId?: string | null;
  /** MCP-push info for a row, or `null` (EMR-277 Layout versions modal indicator). */
  getRemotePush: (templateId: string) => RemotePushInfo | null;
  /** Portal menus into the dialog panel (Layout versions modal). */
  menuPortalContainer?: HTMLElement | null;
  onCreateLayout: () => void;
  onGenerateLayout: () => void;
  onGenerateLayoutWithCustomPrompt: () => void;
  onAddLayoutFromClipboard: () => void;
  onEditTemplate: (template: BespokeTemplateResponse) => void;
  onEditTemplateDetails: (
    template: BespokeTemplateResponse,
    details: { name: string; description: string }
  ) => Promise<void>;
  onCopyTemplate: (template: BespokeTemplateResponse) => void;
  onPasteTemplate: (template: BespokeTemplateResponse) => void;
  onSetTemplateStatus: (
    template: BespokeTemplateResponse,
    status: BespokeStatus
  ) => void;
  onDeleteTemplate: (template: BespokeTemplateResponse) => void;
}) {
  const generating = generationStatus?.state === 'loading';
  const { primary, standby } = resolveBespokePrimaryAndStandby(templates);
  const layoutStateByTemplateId = useMemo(() => {
    if (!questions) {
      return null;
    }
    return new Map(
      templates.map(template => [
        template.id,
        getSectionLayoutState(template, questions),
      ])
    );
  }, [questions, templates]);
  const statusControlsDisabled = !!busyTemplateId;
  const [detailsEditTemplate, setDetailsEditTemplate] =
    useState<BespokeTemplateResponse | null>(null);
  const [detailsSaving, setDetailsSaving] = useState(false);

  const handleSaveDetails = async (details: {
    name: string;
    description: string;
  }) => {
    if (!detailsEditTemplate) return;
    setDetailsSaving(true);
    try {
      await onEditTemplateDetails(detailsEditTemplate, details);
      setDetailsEditTemplate(null);
    } finally {
      setDetailsSaving(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className='space-y-3'>
        <div className='flex items-center justify-between gap-3'>
          <div>
            <h3 className='text-sm font-semibold'>
              Templates for {sectionDisplayName}
            </h3>
          </div>
          <div className='flex gap-2'>
            <Button size='sm' variant='outline' onClick={onCreateLayout}>
              Create new
            </Button>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  className='w-8 shrink-0 p-0'
                  aria-label='Layout generation options'
                >
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                container={menuPortalContainer ?? undefined}
                className={DROPDOWN_MENU_CONTENT_CLASS}
              >
                <DropdownMenuItem
                  onClick={onGenerateLayout}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className='h-3.5 w-3.5 mr-2 animate-spin' />
                  ) : (
                    <Sparkles className='h-3.5 w-3.5 mr-2' />
                  )}
                  Generate with AI
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onGenerateLayoutWithCustomPrompt}
                  disabled={generating}
                >
                  <Pencil className='h-3.5 w-3.5 mr-2' />
                  Generate with custom prompt...
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAddLayoutFromClipboard}>
                  <ClipboardPaste className='h-3.5 w-3.5 mr-2' />
                  Add layout from clipboard
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {templates.length === 0 ? (
          <div className='rounded-md border border-dashed px-3 py-6 text-center'>
            <p className='text-sm font-medium'>No templates yet</p>
            <p className='text-xs text-muted-foreground'>
              Create or generate a Roger Layout Schema for this section.
            </p>
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Active template
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='cursor-help text-xs text-muted-foreground underline decoration-dotted'>
                      What is this?
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs text-left' side='bottom'>
                    <p>
                      <strong>Live</strong> layouts are used when rendering this
                      section. <strong>Paused</strong> keeps the template in the
                      active slot without rendering it. <strong>Standby</strong>{' '}
                      templates are drafts or alternates until promoted.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {primary ? (
                <div className='overflow-hidden rounded-lg border border-primary/35 bg-muted/25'>
                  <div className='p-4'>
                    <div className='flex items-start justify-between gap-3'>
                      <div className='min-w-0 flex-1 space-y-1.5'>
                        <div className='flex flex-wrap items-center gap-2'>
                          <span className='font-medium leading-tight'>
                            {primary.name}
                          </span>
                          <StatusBadge status={primary.status} />
                          {getRemotePush(primary.id) ? (
                            <RemotePushDot info={getRemotePush(primary.id)!} />
                          ) : null}
                          {layoutStateByTemplateId ? (
                            <SectionLayoutValidationLabel
                              state={layoutStateByTemplateId.get(primary.id)!}
                            />
                          ) : null}
                        </div>
                        <p className='text-xs leading-snug text-muted-foreground line-clamp-2 whitespace-normal break-words'>
                          {primary.description?.trim()
                            ? primary.description
                            : 'No description'}
                        </p>
                      </div>
                      <TemplateActionsMenu
                        template={primary}
                        variant='active'
                        statusControlsDisabled={statusControlsDisabled}
                        menuPortalContainer={menuPortalContainer}
                        onEditTemplate={onEditTemplate}
                        onStartEditDetails={setDetailsEditTemplate}
                        onCopyTemplate={onCopyTemplate}
                        onPasteTemplate={onPasteTemplate}
                        onDeleteTemplate={onDeleteTemplate}
                        onSetTemplateStatus={onSetTemplateStatus}
                      />
                    </div>
                  </div>
                  <div className='flex flex-col gap-3 border-t border-border/60 bg-background/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='text-xs tabular-nums text-muted-foreground'>
                      Updated{' '}
                      {primary.updated_at
                        ? formatDateForDisplay(primary.updated_at) || '—'
                        : '—'}
                    </p>
                    <div
                      className='inline-flex shrink-0 self-start rounded-lg border border-border/80 bg-muted/40 p-1 shadow-sm sm:self-auto'
                      role='group'
                      aria-label='Template visibility'
                    >
                      <Button
                        type='button'
                        size='sm'
                        variant={
                          primary.status === 'live' ? 'default' : 'ghost'
                        }
                        className='h-8 min-w-[4.25rem] rounded-md px-3'
                        disabled={statusControlsDisabled}
                        onClick={() => onSetTemplateStatus(primary, 'live')}
                      >
                        Live
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant={
                          primary.status === 'paused' ? 'default' : 'ghost'
                        }
                        className='h-8 min-w-[4.25rem] rounded-md px-3'
                        disabled={statusControlsDisabled}
                        onClick={() => onSetTemplateStatus(primary, 'paused')}
                      >
                        Paused
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='rounded-md border border-dashed border-muted-foreground/25 bg-muted/10 px-3 py-4 text-center text-xs text-muted-foreground'>
                  No active template (<strong>Live</strong> or{' '}
                  <strong>Paused</strong>). Promote one from Standby below.
                </div>
              )}
            </div>

            <div className='space-y-2'>
              <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Standby templates
              </h4>
              {standby.length === 0 ? (
                <p className='rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground'>
                  No standby templates.
                </p>
              ) : (
                <div className='rounded-md border'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead className='w-[160px]'>Description</TableHead>
                        <TableHead className='w-[100px]'>Status</TableHead>
                        {layoutStateByTemplateId ? (
                          <TableHead className='w-[88px]'>Validation</TableHead>
                        ) : null}
                        <TableHead>Updated</TableHead>
                        <TableHead className='text-right w-[72px]'>
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {standby.map(template => (
                        <TableRow
                          key={template.id}
                          className={
                            template.status !== 'standby'
                              ? 'bg-amber-500/5'
                              : undefined
                          }
                        >
                          <TableCell className='font-medium'>
                            {template.name}
                          </TableCell>
                          <TableCell className='w-[160px] max-w-[160px] text-muted-foreground'>
                            <div className='line-clamp-2 whitespace-normal break-words'>
                              {template.description || '—'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center gap-1.5'>
                              <StatusBadge status={template.status} />
                              {getRemotePush(template.id) ? (
                                <RemotePushDot
                                  info={getRemotePush(template.id)!}
                                />
                              ) : null}
                            </div>
                          </TableCell>
                          {layoutStateByTemplateId ? (
                            <TableCell>
                              <SectionLayoutValidationLabel
                                state={layoutStateByTemplateId.get(
                                  template.id
                                )!}
                              />
                            </TableCell>
                          ) : null}
                          <TableCell className='text-muted-foreground'>
                            {template.updated_at
                              ? formatDateForDisplay(template.updated_at) || '—'
                              : '—'}
                          </TableCell>
                          <TableCell className='text-right'>
                            <div className='flex justify-end'>
                              <TemplateActionsMenu
                                template={template}
                                variant='standby'
                                statusControlsDisabled={statusControlsDisabled}
                                menuPortalContainer={menuPortalContainer}
                                onEditTemplate={onEditTemplate}
                                onStartEditDetails={setDetailsEditTemplate}
                                onCopyTemplate={onCopyTemplate}
                                onPasteTemplate={onPasteTemplate}
                                onDeleteTemplate={onDeleteTemplate}
                                onSetTemplateStatus={onSetTemplateStatus}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={detailsEditTemplate != null}
        onOpenChange={open => {
          if (!open && !detailsSaving) setDetailsEditTemplate(null);
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Edit name &amp; description</DialogTitle>
          </DialogHeader>
          {detailsEditTemplate ? (
            <SectionTemplateDetailsEdit
              initialName={detailsEditTemplate.name}
              initialDescription={detailsEditTemplate.description ?? ''}
              saving={detailsSaving}
              onCancel={() => setDetailsEditTemplate(null)}
              onSave={details => void handleSaveDetails(details)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
